<template>
  <div class="px-3 py-3 pb-10 space-y-4 max-w-screen-2xl mx-auto">
    <!-- Header: team + date -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div class="min-w-0">
        <h1 class="text-lg font-bold leading-tight text-gray-900 dark:text-gray-50">Daily Check-In</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400">Post your update, see who's checked in, and keep a documented history.</p>
      </div>
      <div class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <v-autocomplete
          v-model="selectedTeam"
          :items="teams"
          label="Team"
          density="compact"
          variant="solo-filled"
          flat
          hide-details
          prepend-inner-icon="mdi-account-group-outline"
          @update:modelValue="loadFeed"
        ></v-autocomplete>
        <v-text-field
          v-model="date"
          type="date"
          label="Date"
          density="compact"
          variant="solo-filled"
          flat
          hide-details
          prepend-inner-icon="mdi-calendar"
          @update:modelValue="onDateChange"
        ></v-text-field>
      </div>
      <!-- Segmented control drives the same `tab` state as the old toggle -->
      <div class="mt-3 inline-flex w-full rounded-lg bg-gray-100 p-0.5 text-sm font-semibold dark:bg-gray-800 sm:w-auto">
        <button type="button" @click="tab = 'today'"
          class="flex-1 rounded-md px-4 py-1.5 transition sm:flex-none"
          :class="tab === 'today' ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-50' : 'text-gray-500 dark:text-gray-400'">Today</button>
        <button type="button" @click="tab = 'history'"
          class="flex-1 rounded-md px-4 py-1.5 transition sm:flex-none"
          :class="tab === 'history' ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-50' : 'text-gray-500 dark:text-gray-400'">History</button>
      </div>
    </div>

    <template v-if="tab === 'today'">
      <div class="grid grid-cols-1 gap-3 md:grid-cols-12">
        <!-- My check-in form (only for my own team) -->
        <section v-if="selectedTeam === myTeam && myId" class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 md:col-span-5">
          <div class="mb-3 flex items-center justify-between gap-2">
            <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">My check-in</h2>
            <button type="button" @click="prefill"
              class="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition active:scale-95 dark:bg-gray-800 dark:text-gray-200">
              <v-icon size="15" :class="prefilling ? 'animate-spin' : ''">{{ prefilling ? 'mdi-loading' : 'mdi-auto-fix' }}</v-icon>
              Pre-fill
            </button>
          </div>

          <div class="space-y-2.5">
            <v-textarea v-model="form.yesterday" label="What I did yesterday" rows="3" density="compact" variant="solo-filled" flat hide-details auto-grow></v-textarea>
            <v-textarea v-model="form.today" label="What I'll do today" rows="3" density="compact" variant="solo-filled" flat hide-details auto-grow></v-textarea>
            <v-textarea v-model="form.blockers" label="Blockers (if any)" rows="2" density="compact" variant="solo-filled" flat hide-details auto-grow></v-textarea>

            <!-- Mood pills drive the same `form.mood` state as the old select -->
            <div>
              <p class="mb-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Mood</p>
              <div class="flex flex-wrap gap-1.5">
                <button v-for="m in moods" :key="m.value" type="button"
                  @click="form.mood = form.mood === m.value ? null : m.value"
                  class="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition active:scale-95"
                  :style="form.mood === m.value ? { background: m.color, color: '#fff' } : {}"
                  :class="form.mood === m.value ? 'shadow-sm' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'">
                  <v-icon size="15">{{ m.icon }}</v-icon>{{ m.title }}
                </button>
              </div>
            </div>

            <v-text-field v-model="form.ticket_refs" label="Related tickets" density="compact" variant="solo-filled" flat prepend-inner-icon="mdi-ticket-outline" hint="Auto-filled from your open tickets on pre-fill" persistent-hint></v-text-field>
          </div>

          <button type="button" @click="save" :disabled="!form.today && !form.yesterday"
            class="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-2.5 text-sm font-bold text-gray-900 shadow-sm transition active:scale-95 disabled:opacity-50">
            <v-icon v-if="saving" size="18" class="animate-spin">mdi-loading</v-icon>
            Save check-in
          </button>
        </section>

        <!-- Team status (name + status + mood only) -->
        <section class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900"
          :class="selectedTeam === myTeam && myId ? 'md:col-span-7' : 'md:col-span-12'">
          <div class="mb-3 flex items-center justify-between gap-2">
            <h2 class="min-w-0 truncate text-sm font-bold text-gray-900 dark:text-gray-50">{{ selectedTeam || 'Team' }} — {{ formattedDate(date) }}</h2>
            <span class="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold"
              :class="submittedCount === feed.length && feed.length ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' : 'bg-amber-400 text-gray-900'">
              {{ submittedCount }}/{{ feed.length }} checked in
            </span>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="space-y-2">
            <div v-for="n in 5" :key="'fl-' + n" class="h-14 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800"></div>
          </div>

          <!-- Empty -->
          <EmptyState v-else-if="feed.length === 0" dense icon="mdi-account-group-outline" title="No members in this team" />

          <!-- Feed rows -->
          <div v-else class="space-y-2">
            <div v-for="row in feed" :key="row.user_id"
              class="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
              <div class="flex min-w-0 items-center gap-2.5">
                <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">{{ initials(row.fullname) }}</span>
                <div class="min-w-0">
                  <p class="truncate text-sm font-semibold text-gray-900 dark:text-gray-50">{{ row.fullname }}</p>
                  <p class="truncate text-xs text-gray-500 dark:text-gray-400">{{ row.role }}</p>
                </div>
              </div>
              <div class="flex shrink-0 items-center gap-1.5">
                <span v-if="row.standup && row.standup.mood"
                  class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                  :style="moodChipStyle(row.standup.mood)">
                  <v-icon size="13">{{ moodIcon(row.standup.mood) }}</v-icon>{{ row.standup.mood }}
                </span>
                <span class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  :class="row.submitted ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'">
                  <v-icon size="13">{{ row.submitted ? 'mdi-check-circle' : 'mdi-clock-outline' }}</v-icon>{{ row.submitted ? 'Submitted' : 'Pending' }}
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </template>

    <!-- History -->
    <template v-else>
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <v-text-field v-model="histFrom" type="date" label="From" density="compact" variant="solo-filled" flat hide-details></v-text-field>
          <v-text-field v-model="histTo" type="date" label="To" density="compact" variant="solo-filled" flat hide-details></v-text-field>
          <div class="col-span-2 flex gap-2 sm:justify-end">
            <button type="button" @click="loadHistory"
              class="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-amber-400 px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm transition active:scale-95 sm:flex-none">
              <v-icon size="18">mdi-magnify</v-icon> Search
            </button>
            <button type="button" @click="exportCsv" :disabled="!historyRows.length"
              class="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition active:scale-95 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 sm:flex-none">
              <v-icon size="18">mdi-download</v-icon> Export
            </button>
          </div>
        </div>

        <!-- Empty -->
        <EmptyState v-if="!historyRows.length" dense icon="mdi-history" title="No check-ins found" subtitle="Adjust the team or date range." />

        <!-- Check-in cards -->
        <div v-else class="mt-3 space-y-2">
          <div v-for="item in historyRows" :key="item.id"
            class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="truncate text-sm font-bold text-gray-900 dark:text-gray-50">{{ item.fullname }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ formattedDate(item.standup_date) }}</p>
              </div>
              <span v-if="item.mood"
                class="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                :style="moodChipStyle(item.mood)">
                <v-icon size="13">{{ moodIcon(item.mood) }}</v-icon>{{ item.mood }}
              </span>
            </div>

            <div class="mt-2.5 space-y-2 text-xs">
              <div>
                <p class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Today</p>
                <p class="line-clamp-2 text-gray-700 dark:text-gray-300">{{ item.today || '—' }}</p>
              </div>
              <div>
                <p class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Yesterday</p>
                <p class="line-clamp-2 text-gray-700 dark:text-gray-300">{{ item.yesterday || '—' }}</p>
              </div>
              <div v-if="item.blockers">
                <p class="text-[10px] font-semibold uppercase tracking-wide text-rose-400">Blockers</p>
                <p class="line-clamp-2 font-medium text-rose-600 dark:text-rose-400">{{ item.blockers }}</p>
              </div>
            </div>

            <div class="mt-2.5 flex items-center justify-end gap-1 border-t border-gray-100 pt-2 dark:border-gray-800">
              <button type="button" aria-label="View" @click="openView(item)"
                class="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"><v-icon size="18">mdi-eye-outline</v-icon></button>
              <button type="button" aria-label="Edit" @click="openEdit(item)"
                class="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"><v-icon size="18">mdi-pencil-outline</v-icon></button>
              <button type="button" aria-label="Delete" @click="confirmDelete(item)"
                class="rounded-lg p-1.5 text-rose-500 transition hover:bg-rose-50 dark:hover:bg-rose-500/10"><v-icon size="18">mdi-delete-outline</v-icon></button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- View dialog -->
    <v-dialog v-model="viewDialog" max-width="560" scrollable>
      <v-card v-if="selected">
        <div class="d-flex align-start justify-space-between pa-4">
          <div class="text-left">
            <div class="text-caption text-grey">{{ formattedDate(selected.standup_date) }} · {{ selected.team }}</div>
            <div class="text-h6 font-weight-bold">{{ selected.fullname }}</div>
          </div>
          <v-chip v-if="selected.mood" size="small" variant="flat" :style="moodChipStyle(selected.mood)" :prepend-icon="moodIcon(selected.mood)">{{ selected.mood }}</v-chip>
        </div>
        <v-divider></v-divider>
        <v-card-text class="text-left">
          <div class="vt-block"><div class="vt-label">Yesterday</div><div class="vt-text">{{ selected.yesterday || '—' }}</div></div>
          <div class="vt-block"><div class="vt-label">Today</div><div class="vt-text">{{ selected.today || '—' }}</div></div>
          <div class="vt-block"><div class="vt-label" :class="{ 'text-error': selected.blockers }">Blockers</div><div class="vt-text">{{ selected.blockers || 'None' }}</div></div>
          <div v-if="selected.ticket_refs" class="vt-block"><div class="vt-label">Related tickets</div><div class="vt-text">{{ selected.ticket_refs }}</div></div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="viewDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit dialog -->
    <v-dialog v-model="editDialog" max-width="560" scrollable>
      <v-card v-if="selected">
        <v-card-title class="text-subtitle-1 font-weight-bold">Edit check-in — {{ selected.fullname }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-textarea v-model="editForm.yesterday" label="Yesterday" rows="3" density="compact" variant="outlined" auto-grow></v-textarea>
          <v-textarea v-model="editForm.today" label="Today" rows="3" density="compact" variant="outlined" auto-grow></v-textarea>
          <v-textarea v-model="editForm.blockers" label="Blockers" rows="2" density="compact" variant="outlined" auto-grow></v-textarea>
          <v-select
            v-model="editForm.mood"
            :items="moods"
            item-title="title"
            item-value="value"
            label="Mood"
            density="compact"
            variant="outlined"
            clearable
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props" :title="item.raw.title">
                <template v-slot:prepend><v-icon :color="item.raw.color">{{ item.raw.icon }}</v-icon></template>
              </v-list-item>
            </template>
          </v-select>
          <v-text-field v-model="editForm.ticket_refs" label="Related tickets" density="compact" variant="outlined" prepend-inner-icon="mdi-ticket-outline"></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" :disabled="savingEdit" @click="editDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :loading="savingEdit" @click="saveEdit">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirm -->
    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card v-if="selected" title="Delete check-in" prepend-icon="mdi-alert">
        <v-card-text>
          Delete <strong>{{ selected.fullname }}</strong>'s check-in for
          <strong>{{ formattedDate(selected.standup_date) }}</strong>? This can't be undone.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" :disabled="deleting" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="deleting" @click="doDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Snackbar :toggle-snackbar="isshow" :response-message="message"></Snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import constant from '@/constant';
import apiMixin from '@/mixins/apiMixin';
import formatMixin from '@/mixins/formatMixin';
import EmptyState from '@/components/ui/EmptyState.vue';
import Snackbar from './Snackbar.vue';
import saveCsv from '@/utils/exportCsv';

export default {
  name: 'StandupPage',
  components: { EmptyState, Snackbar },
  mixins: [apiMixin, formatMixin],
  data() {
    return {
      constant,
      user_data: null,
      teams: [],
      selectedTeam: null,
      date: this.todayStr(),
      tab: 'today',
      moods: [
        { title: 'Great', value: 'Great', icon: 'mdi-emoticon-excited', color: '#2e9e5b' },
        { title: 'Good', value: 'Good', icon: 'mdi-emoticon-happy', color: '#3aa76d' },
        { title: 'Okay', value: 'Okay', icon: 'mdi-emoticon-neutral', color: '#e0900a' },
        { title: 'Tired', value: 'Tired', icon: 'mdi-emoticon-sad', color: '#e5733a' },
        { title: 'Blocked', value: 'Blocked', icon: 'mdi-emoticon-dead', color: '#e5484d' },
      ],
      form: { yesterday: '', today: '', blockers: '', mood: null, ticket_refs: '' },
      feed: [],
      loading: false,
      saving: false,
      prefilling: false,
      histFrom: '',
      histTo: '',
      historyRows: [],
      histHeaders: [
        { title: 'Date', key: 'standup_date', sortable: true },
        { title: 'Member', key: 'fullname', sortable: true },
        { title: 'Yesterday', key: 'yesterday', sortable: false },
        { title: 'Today', key: 'today', sortable: false },
        { title: 'Blockers', key: 'blockers', sortable: false },
        { title: 'Mood', key: 'mood', sortable: true },
        { title: '', key: 'actions', sortable: false, align: 'end' },
      ],
      selected: null,
      viewDialog: false,
      editDialog: false,
      deleteDialog: false,
      savingEdit: false,
      deleting: false,
      editForm: { yesterday: '', today: '', blockers: '', mood: null, ticket_refs: '' },
      isshow: false,
      message: '',
    };
  },
  computed: {
    myId() {
      const e = this.user_data && this.user_data.employee_information;
      return e ? e.id : null;
    },
    myTeam() {
      const e = this.user_data && this.user_data.employee_information;
      return e ? e.team : null;
    },
    submittedCount() {
      return this.feed.filter((r) => r.submitted).length;
    },
  },
  methods: {
    todayStr() {
      const d = new Date();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${m}-${day}`;
    },
    notify(msg) {
      this.message = msg;
      this.isshow = true;
      setTimeout(() => { this.isshow = false; }, 1500);
    },
    clip(v) {
      const s = String(v || '');
      return s.length > 60 ? s.slice(0, 60) + '…' : s;
    },
    moodIcon(mood) {
      const m = this.moods.find((x) => x.value === mood);
      return m ? m.icon : 'mdi-emoticon-outline';
    },
    moodColor(mood) {
      const m = this.moods.find((x) => x.value === mood);
      return m ? m.color : '#9aa0aa';
    },
    // Lively filled chip: mood color background, white text/icon.
    moodChipStyle(mood) {
      return { background: this.moodColor(mood), color: '#fff' };
    },
    initials(name) {
      const parts = (name || 'U').replace(/[^a-zA-Z ]/g, '').trim().split(/\s+/);
      return ((parts[0] && parts[0][0]) || '' + (parts[1] && parts[1][0] || '')).toUpperCase() || 'U';
    },
    getTeams() {
      axios.get(utils._api(this.constant.get_teams), this.authHeaders())
        .then((response) => { this.teams = (response.data.data || []).map((t) => t.team_name); })
        .catch((error) => console.error('Error fetching teams:', error));
    },
    loadFeed() {
      if (!this.selectedTeam) return;
      this.loading = true;
      axios.post(utils._api(this.constant.standup_list), { team: this.selectedTeam, date: this.date }, this.authHeaders())
        .then((response) => {
          const data = response.data.data || {};
          this.feed = data.entries || [];
          if (this.selectedTeam === this.myTeam && this.myId) {
            const mine = this.feed.find((r) => r.user_id === this.myId);
            if (mine && mine.standup) {
              this.form = {
                yesterday: mine.standup.yesterday || '',
                today: mine.standup.today || '',
                blockers: mine.standup.blockers || '',
                mood: mine.standup.mood || null,
                ticket_refs: mine.standup.ticket_refs || '',
              };
            }
          }
        })
        .catch((error) => console.error('Error loading feed:', error))
        .finally(() => { this.loading = false; });
    },
    onDateChange() {
      this.loadFeed();
      if (this.tab === 'history') this.loadHistory();
    },
    prefill() {
      if (!this.myId) return;
      this.prefilling = true;
      axios.post(utils._api(this.constant.standup_prefill), { user_id: this.myId, date: this.date }, this.authHeaders())
        .then((response) => {
          const d = response.data.data || {};
          if (d.yesterday) this.form.yesterday = d.yesterday;
          if (d.today) this.form.today = d.today;
          this.form.ticket_refs = d.ticket_refs || '';
        })
        .catch((error) => { console.error('Error prefilling:', error); this.notify('Could not pre-fill'); })
        .finally(() => { this.prefilling = false; });
    },
    save() {
      if (!this.myId) return;
      this.saving = true;
      const payload = {
        user_id: this.myId,
        standup_date: this.date,
        yesterday: this.form.yesterday || null,
        today: this.form.today || null,
        blockers: this.form.blockers || null,
        mood: this.form.mood || null,
        ticket_refs: this.form.ticket_refs || null,
      };
      axios.post(utils._api(this.constant.standup_submit), payload, this.authHeaders())
        .then(() => { this.notify('Check-in saved'); this.loadFeed(); })
        .catch((error) => { console.error('Error saving check-in:', error); this.notify('Failed to save'); })
        .finally(() => { this.saving = false; });
    },
    loadHistory() {
      this.historyRows = [];
      const payload = { team: this.selectedTeam || null };
      if (this.histFrom) payload.from = this.histFrom;
      if (this.histTo) payload.to = this.histTo;
      axios.post(utils._api(this.constant.standup_history), payload, this.authHeaders())
        .then((response) => { this.historyRows = response.data.data || []; })
        .catch((error) => console.error('Error loading history:', error));
    },
    openView(row) {
      this.selected = row;
      this.viewDialog = true;
    },
    openEdit(row) {
      this.selected = row;
      this.editForm = {
        yesterday: row.yesterday || '',
        today: row.today || '',
        blockers: row.blockers || '',
        mood: row.mood || null,
        ticket_refs: row.ticket_refs || '',
      };
      this.editDialog = true;
    },
    saveEdit() {
      if (!this.selected) return;
      this.savingEdit = true;
      const payload = {
        user_id: this.selected.user_id,
        standup_date: this.selected.standup_date,
        yesterday: this.editForm.yesterday || null,
        today: this.editForm.today || null,
        blockers: this.editForm.blockers || null,
        mood: this.editForm.mood || null,
        ticket_refs: this.editForm.ticket_refs || null,
      };
      axios.post(utils._api(this.constant.standup_submit), payload, this.authHeaders())
        .then(() => { this.notify('Check-in updated'); this.editDialog = false; this.loadHistory(); })
        .catch((error) => { console.error('Error updating check-in:', error); this.notify('Failed to update'); })
        .finally(() => { this.savingEdit = false; });
    },
    confirmDelete(row) {
      this.selected = row;
      this.deleteDialog = true;
    },
    doDelete() {
      if (!this.selected) return;
      this.deleting = true;
      axios.post(utils._api(this.constant.standup_delete), { id: this.selected.id }, this.authHeaders())
        .then(() => { this.notify('Check-in deleted'); this.deleteDialog = false; this.loadHistory(); })
        .catch((error) => { console.error('Error deleting check-in:', error); this.notify('Failed to delete'); })
        .finally(() => { this.deleting = false; });
    },
    exportCsv() {
      const cols = ['standup_date', 'fullname', 'team', 'yesterday', 'today', 'blockers', 'mood', 'ticket_refs'];
      const esc = (v) => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
      const lines = [cols.join(',')];
      this.historyRows.forEach((r) => lines.push(cols.map((c) => esc(r[c])).join(',')));
      saveCsv(`checkins_${this.selectedTeam || 'all'}.csv`, lines.join('\n'));
    },
  },
  watch: {
    tab(val) {
      if (val === 'history') this.loadHistory();
    },
  },
  created() {
    try {
      const raw = localStorage.getItem('user_data');
      this.user_data = raw ? JSON.parse(raw) : null;
    } catch (e) { this.user_data = null; }
    this.selectedTeam = this.myTeam;
  },
  mounted() {
    this.getTeams();
    if (this.selectedTeam) this.loadFeed();
  },
};
</script>

<style scoped>
.su-row {
  padding: 12px 0;
  border-bottom: 1px solid var(--int-border);
}
.su-row:last-child { border-bottom: none; }

.vt-block { margin-bottom: 12px; }
.vt-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--int-text-faint);
  margin-bottom: 2px;
}
.vt-text { font-size: 0.9rem; white-space: pre-wrap; color: var(--int-text); }
</style>
